import { datadogLogs } from "@datadog/browser-logs";

import { discoverCaaEndpoints } from "@repo/shopifyV2/utils/caaDiscovery";

import { SHOPIFY_DOMAIN } from "@const/urls";

import { refreshHikoCaaToken } from "@lib/hikoHeadlessSession";

import type { ICaaToken } from "@store/customer";
import { useCustomerStore } from "@store/customer";

/**
 * Customer Account API 전용 Apollo client.
 * jp locale에서만 사용 — 기존 `lib/apollo.ts`의 싱글턴 client(Storefront API)는 그대로 유지
 */

const JP_SHOP_DOMAIN = SHOPIFY_DOMAIN.jp.replace(/^https?:\/\//, "").replace(/\/$/, "");

const CAA_CLIENT_ID = import.meta.env.VITE_JP_CAA_CLIENT_ID;

// access_token 만료 60초 전부터는 미리 갱신
const EXPIRY_BUFFER_MS = 60_000;

// 동시에 여러 요청이 만료 임박을 감지해도 refresh 호출은 한 번만 나가도록 in-flight 요청을 공유
let inFlightRefresh: Promise<ICaaToken> | null = null;

const refreshCaaToken = (refreshToken: string): Promise<ICaaToken> => {
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async () => {
    const { tokenEndpoint } = await discoverCaaEndpoints(JP_SHOP_DOMAIN);

    const response = await fetch(tokenEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "refresh_token",
        client_id: CAA_CLIENT_ID,
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      // refresh_token 만료/폐기 등으로 갱신 실패 시 store를 정리 — 무효한 토큰으로 매 요청마다 재시도되는 것을 방지
      useCustomerStore.getState().logout();
      throw new Error(`[CAA] token refresh failed: ${response.status}`);
    }

    const data = (await response.json()) as {
      access_token: string;
      refresh_token: string;
      id_token: string;
      expires_in: number;
    };

    const caaToken: ICaaToken = {
      accessToken: data.access_token,
      // Shopify가 refresh 시 새 refresh_token을 내려줄 수 있어 매번 덮어씌움
      refreshToken: data.refresh_token,
      idToken: data.id_token,
      expiresAt: new Date(Date.now() + data.expires_in * 1000).toISOString(),
    };

    useCustomerStore.getState().loginCaa(caaToken);

    return caaToken;
  })().finally(() => {
    inFlightRefresh = null;
  });

  return inFlightRefresh;
};

/**
 * Customer Account API GraphQL 실행기.
 * 매 요청마다 endpoint discovery + 토큰 만료 검사(60초 버퍼) 후 갱신한다.
 */
/**
 * 만료가 임박했으면 갱신한 뒤의 CAA 토큰을 반환한다.
 * Storefront API의 buyerIdentity에 jp 토큰을 실어보낼 때처럼, caaRequest 바깥에서도 유효한
 * access token이 필요한 곳에서 사용한다.
 */
export const getFreshCaaToken = async (): Promise<ICaaToken | null> => {
  let { caaToken } = useCustomerStore.getState();

  if (caaToken && new Date(caaToken.expiresAt).getTime() - Date.now() < EXPIRY_BUFFER_MS) {
    // "hiko" 세션은 refresh_token을 직접 보유하지 않으므로 위젯에 갱신을 위임
    caaToken = caaToken.source === "hiko" ? await refreshHikoCaaToken() : await refreshCaaToken(caaToken.refreshToken);
  }

  return caaToken;
};

export const caaRequest = async <TData>(document: string, variables?: Record<string, unknown>): Promise<TData> => {
  const { graphqlApiEndpoint } = await discoverCaaEndpoints(JP_SHOP_DOMAIN);

  const caaToken = await getFreshCaaToken();

  const response = await fetch(graphqlApiEndpoint, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(caaToken?.accessToken ? { Authorization: caaToken.accessToken } : {}),
    },
    body: JSON.stringify({ query: document, variables }),
  });

  if (!response.ok) {
    datadogLogs.logger.error("[CAA] Network error", { status: response.status });
    throw new Error(`[CAA] Network error : ${response.status}`);
  }

  const json = (await response.json()) as { data?: TData; errors?: { message: string; path?: unknown }[] };

  if (json.errors?.length) {
    json.errors.forEach(({ message, path }) => {
      datadogLogs.logger.error("[CAA] GraphQL error", { message, path: JSON.stringify(path) });
    });
    throw new Error(`[CAA] GraphQL error : ${json.errors.map((e) => e.message).join(", ")}`);
  }

  return json.data as TData;
};

export default caaRequest;
